/**
 * Invoice Card Component
 *
 * A card-based display for invoice information, designed for mobile-friendly viewing.
 * Shows household, student, payment status, and financial summary.
 */

import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  HStack,
  Icon,
  SimpleGrid,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react"
import {
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiCreditCard,
  FiUser,
} from "react-icons/fi"

import type { EnrichedInvoice } from "@/hooks/api/useInvoices"

// Extended invoice type with verification fields
interface InvoiceWithVerification extends EnrichedInvoice {
  verification_status?: string | null
  rejection_reason?: string | null
  rejection_notes?: string | null
}

// Payment status colors and labels
const statusConfig: Record<
  string,
  { color: string; icon: React.ReactNode; label: string }
> = {
  paid: {
    color: "green",
    icon: <FiCheckCircle />,
    label: "Paid",
  },
  partial: {
    color: "orange",
    icon: <FiAlertCircle />,
    label: "Partial",
  },
  pending: {
    color: "red",
    icon: <FiAlertCircle />,
    label: "Unpaid",
  },
  refunded: {
    color: "gray",
    icon: <FiClock />,
    label: "Refunded",
  },
}

// Verification status config
const verificationConfig: Record<string, { color: string; label: string }> = {
  pending_verification: { color: "yellow", label: "Pending Verification" },
  approved: { color: "green", label: "Verified" },
  rejected: { color: "red", label: "Rejected" },
}

function formatCurrency(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return "$0.00"
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount)
}

export interface InvoiceCardProps {
  invoice: InvoiceWithVerification
  /** Whether this is a parent view (shows "Confirm Payment" instead of "Record Payment") */
  isParentView?: boolean
  /** Whether the user can update invoices (staff) */
  canUpdate?: boolean
  /** Handler for staff "Record Payment" action */
  onRecordPayment?: (invoice: EnrichedInvoice) => void
  /** Handler for parent "Confirm Payment" action */
  onConfirmPayment?: (invoice: InvoiceWithVerification) => void
  /** Handler for staff "Approve" action */
  onApprove?: (invoiceId: string) => void
  /** Handler for staff "Reject" action */
  onReject?: (invoiceId: string) => void
  /** Whether approve action is loading */
  isApproving?: boolean
}

export function InvoiceCard({
  invoice,
  isParentView = false,
  canUpdate = false,
  onRecordPayment,
  onConfirmPayment,
  onApprove,
  onReject,
  isApproving = false,
}: InvoiceCardProps) {
  const paymentStatus = invoice.payment_status || "pending"
  const paymentInfo = statusConfig[paymentStatus] || statusConfig.pending

  const amountDue = invoice.amount_due ?? 0
  const amountPaid = invoice.amount_paid ?? 0
  const balance = amountDue - amountPaid
  const isPaid = balance <= 0

  const verificationStatus = invoice.verification_status
  const verificationInfo = verificationStatus
    ? verificationConfig[verificationStatus]
    : null

  return (
    <Card.Root
      bg="white"
      borderRadius="xl"
      boxShadow="sm"
      borderWidth="1px"
      borderColor="gray.100"
      _hover={{
        boxShadow: "md",
        borderColor: "gray.200",
      }}
      transition="all 0.2s"
    >
      <Card.Body p={5}>
        <VStack align="stretch" gap={4}>
          {/* Header: Household name + payment status badge */}
          <Flex justify="space-between" align="start">
            <Box flex={1}>
              <Text fontWeight="semibold" fontSize="lg" color="gray.800">
                {invoice.household_name || "Household"}
              </Text>
              {invoice.household_email && (
                <Text fontSize="sm" color="gray.500">
                  {invoice.household_email}
                </Text>
              )}
            </Box>

            <HStack gap={2}>
              <Badge
                colorPalette={paymentInfo.color}
                variant="subtle"
                px={2}
                py={1}
                borderRadius="full"
              >
                <HStack gap={1}>
                  <Icon fontSize="xs">{paymentInfo.icon}</Icon>
                  <Text>{paymentInfo.label}</Text>
                </HStack>
              </Badge>
            </HStack>
          </Flex>

          {/* Student info */}
          <HStack fontSize="sm" color="gray.600">
            <Icon color="gray.400">
              <FiUser />
            </Icon>
            <Text>{invoice.student_name || "Student"}</Text>
          </HStack>

          {/* Financial summary */}
          <Box
            bg={isPaid ? "green.50" : "orange.50"}
            borderRadius="lg"
            p={3}
          >
            <SimpleGrid columns={3} gap={2}>
              <Box>
                <Text fontSize="xs" color="gray.500">
                  Total
                </Text>
                <Text fontWeight="medium" color="gray.800">
                  {formatCurrency(amountDue)}
                </Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500">
                  Paid
                </Text>
                <Text fontWeight="medium" color="green.600">
                  {formatCurrency(amountPaid)}
                </Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500">
                  Balance
                </Text>
                <Text
                  fontWeight="bold"
                  color={isPaid ? "green.600" : "orange.600"}
                >
                  {formatCurrency(balance)}
                </Text>
              </Box>
            </SimpleGrid>
          </Box>

          {/* Verification badge */}
          {verificationInfo && (
            <Box
              bg={`${verificationInfo.color}.50`}
              borderRadius="md"
              p={3}
              borderLeftWidth="3px"
              borderLeftColor={`${verificationInfo.color}.400`}
            >
              <HStack color={`${verificationInfo.color}.700`} fontSize="sm">
                <Icon>
                  <FiClock />
                </Icon>
                <Text fontWeight="medium">{verificationInfo.label}</Text>
              </HStack>
              {verificationStatus === "pending_verification" && (
                <Text fontSize="xs" color="yellow.600" mt={1}>
                  Your payment confirmation is being reviewed
                </Text>
              )}
            </Box>
          )}

          {/* Actions */}
          <Stack gap={2}>
            {/* Staff: Record Payment button */}
            {canUpdate && balance > 0 && onRecordPayment && (
              <Button
                size="sm"
                minH="44px"
                colorPalette="blue"
                onClick={() => onRecordPayment(invoice)}
              >
                <FiCreditCard />
                Record Payment
              </Button>
            )}

            {/* Parent: Confirm Payment button */}
            {isParentView &&
              balance > 0 &&
              verificationStatus !== "pending_verification" &&
              onConfirmPayment && (
                <Button
                  size="sm"
                  minH="44px"
                  colorPalette="green"
                  onClick={() => onConfirmPayment(invoice)}
                >
                  <FiCheckCircle />
                  Confirm Payment
                </Button>
              )}

            {/* Staff: Approve/Reject for pending verifications */}
            {canUpdate &&
              verificationStatus === "pending_verification" && (
                <HStack gap={2}>
                  {onApprove && (
                    <Button
                      size="sm"
                      minH="44px"
                      flex={1}
                      colorPalette="green"
                      onClick={() => onApprove(invoice.id)}
                      loading={isApproving}
                    >
                      Approve
                    </Button>
                  )}
                  {onReject && (
                    <Button
                      size="sm"
                      minH="44px"
                      flex={1}
                      colorPalette="red"
                      onClick={() => onReject(invoice.id)}
                    >
                      Reject
                    </Button>
                  )}
                </HStack>
              )}

            {/* Paid in full indicator */}
            {isPaid && !verificationInfo && (
              <HStack justify="center" color="green.600" fontSize="sm">
                <Icon>
                  <FiCheckCircle />
                </Icon>
                <Text fontWeight="medium">Paid in Full</Text>
              </HStack>
            )}
          </Stack>
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}

export default InvoiceCard
